import { useEffect, useMemo, useState } from 'react'
import { Network } from 'lucide-react'
import api from '@/services/api'
import { cn } from '@/utils/cn'
import { formatCurrency } from '@/utils/format-currency'
import type { PartnerPyramidNode } from '@/types'

interface PartnersPyramidViewProps {
  version: number
  search: string
  onSelect: (partnerId: string) => void
}

type TreeNode = PartnerPyramidNode & { children: TreeNode[]; pathPercent: number; depth: number }

function buildForest(nodes: PartnerPyramidNode[]): TreeNode[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const childrenOf = new Map<string, PartnerPyramidNode[]>()
  const roots: PartnerPyramidNode[] = []

  for (const node of nodes) {
    if (node.indicadoPorId && byId.has(node.indicadoPorId)) {
      const list = childrenOf.get(node.indicadoPorId) ?? []
      list.push(node)
      childrenOf.set(node.indicadoPorId, list)
    } else {
      roots.push(node)
    }
  }

  const build = (node: PartnerPyramidNode, parentPercent: number, depth: number, seen: Set<string>): TreeNode => {
    const pathPercent = parentPercent + node.comissao
    const nextSeen = new Set(seen).add(node.id)
    return {
      ...node,
      pathPercent,
      depth,
      children: (childrenOf.get(node.id) ?? [])
        .filter((c) => !nextSeen.has(c.id))
        .map((c) => build(c, pathPercent, depth + 1, nextSeen)),
    }
  }

  return roots.map((r) => build(r, 0, 1, new Set()))
}

function treeMatches(node: TreeNode, term: string): boolean {
  return node.nome.toLowerCase().includes(term) || node.children.some((c) => treeMatches(c, term))
}

function countNodes(node: TreeNode): number {
  return 1 + node.children.reduce((sum, c) => sum + countNodes(c), 0)
}

function PyramidCard({ node, maxPercent, onSelect }: { node: TreeNode; maxPercent: number; onSelect: (id: string) => void }) {
  const nearCap = node.pathPercent >= maxPercent * 0.9
  return (
    <button
      type='button'
      onClick={() => onSelect(node.id)}
      className={cn(
        'w-44 rounded-lg border bg-[var(--bg-card)] px-3 py-2.5 text-left shadow-sm transition-colors hover:border-[var(--brand-primary)]',
        node.indicacaoAtiva ? 'border-[var(--border)]' : 'border-dashed border-[var(--border)] opacity-60',
      )}
    >
      <p className='truncate text-sm font-semibold text-[var(--font-color-body)]'>{node.nome}</p>
      {node.empresa && <p className='truncate text-[11px] text-[var(--font-color-muted)]'>{node.empresa}</p>}
      <div className='mt-2 flex items-center justify-between text-[11px]'>
        <span className='rounded bg-[var(--brand-primary-muted)] px-1.5 py-0.5 font-bold text-[var(--brand-primary)]'>
          {node.modoComissao === 'DUAS_MENSALIDADES' ? `Orig. · ${node.comissao}%` : `${node.comissao}%`}
        </span>
        <span className={cn('font-medium', nearCap ? 'text-amber-500' : 'text-[var(--font-color-muted)]')}>
          cadeia {node.pathPercent.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%
        </span>
      </div>
      <p className='mt-1 text-[11px] font-semibold text-emerald-500'>{formatCurrency(node.totalComissoes)}</p>
    </button>
  )
}

function PyramidBranch({ node, maxPercent, onSelect }: { node: TreeNode; maxPercent: number; onSelect: (id: string) => void }) {
  return (
    <div className='flex flex-col items-center'>
      <PyramidCard node={node} maxPercent={maxPercent} onSelect={onSelect} />
      {node.children.length > 0 && (
        <>
          <span className='h-5 w-px bg-[var(--border)]' />
          <div className='flex items-start'>
            {node.children.map((child, index) => (
              <div key={child.id} className='relative flex flex-col items-center px-2 pt-5'>
                {node.children.length > 1 && (
                  <span
                    className='absolute top-0 h-px bg-[var(--border)]'
                    style={{
                      left: index === 0 ? '50%' : 0,
                      right: index === node.children.length - 1 ? '50%' : 0,
                    }}
                  />
                )}
                <span className='absolute left-1/2 top-0 h-5 w-px bg-[var(--border)]' />
                <PyramidBranch node={child} maxPercent={maxPercent} onSelect={onSelect} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export function PartnersPyramidView({ version, search, onSelect }: PartnersPyramidViewProps) {
  const [nodes, setNodes] = useState<PartnerPyramidNode[]>([])
  const [maxPercent, setMaxPercent] = useState(20)
  const [maxLevels, setMaxLevels] = useState(4)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    api
      .get('/parceiros/pyramid', { signal: controller.signal })
      .then((resp) => {
        setNodes(resp.data.data?.nodes ?? [])
        setMaxPercent(resp.data.data?.maxPercent ?? 20)
        setMaxLevels(resp.data.data?.maxLevels ?? 4)
      })
      .catch(() => {
        if (!controller.signal.aborted) setNodes([])
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [version])

  const { networks, solo } = useMemo(() => {
    const term = search.trim().toLowerCase()
    const forest = buildForest(nodes).filter((tree) => !term || treeMatches(tree, term))
    return {
      networks: forest.filter((t) => t.children.length > 0).sort((a, b) => countNodes(b) - countNodes(a)),
      solo: forest.filter((t) => t.children.length === 0),
    }
  }, [nodes, search])

  if (loading) {
    return <div className='glass-card py-12 text-center text-sm text-[var(--font-color-muted)]'>Carregando pirâmide...</div>
  }

  return (
    <div className='space-y-4'>
      <div className='glass-card flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-[var(--font-color-muted)]'>
        <span className='flex items-center gap-1.5 font-semibold text-[var(--font-color-body)]'>
          <Network size={14} className='text-[var(--brand-primary)]' />
          Até {maxLevels} níveis · teto de {maxPercent}% por cadeia
        </span>
        <span>Topo = quem indicou · base = quem traz o cliente (Nível 1)</span>
        <span>“cadeia” = soma das % do topo até o parceiro</span>
      </div>

      {networks.length === 0 && solo.length === 0 && (
        <div className='glass-card py-12 text-center text-sm text-[var(--font-color-muted)]'>Nenhum parceiro encontrado</div>
      )}

      {networks.map((tree) => (
        <div key={tree.id} className='glass-card overflow-x-auto'>
          <p className='mb-4 text-[var(--font-color-label)] uppercase tracking-wider font-medium' style={{ fontSize: 'var(--font-size-label)' }}>
            Rede de {tree.nome} · {countNodes(tree)} parceiros
          </p>
          <div className='flex min-w-max justify-center pb-2'>
            <PyramidBranch node={tree} maxPercent={maxPercent} onSelect={onSelect} />
          </div>
        </div>
      ))}

      {solo.length > 0 && (
        <div className='glass-card'>
          <p className='mb-3 text-[var(--font-color-label)] uppercase tracking-wider font-medium' style={{ fontSize: 'var(--font-size-label)' }}>
            Parceiros sem rede · {solo.length}
          </p>
          <div className='flex flex-wrap gap-3'>
            {solo.map((node) => (
              <PyramidCard key={node.id} node={node} maxPercent={maxPercent} onSelect={onSelect} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
